/**
 * LA VALIDATION QUE LA FINALISATION REMPLACERAIT (16/10/2026).
 *
 * Une nuit validée en bloc (reprise de l'historique, ou validée à l'écran)
 * et qu'on clôture au détail : la clôture finalisée REMPLACE la validation.
 * Le 09/09/2026, validé à 15 436,20 € dont 8 021 € de billetterie
 * d'associations que la clôture ne compte pas, perdait cet argent sans que
 * personne ne le voie. Ce bloc le dit AVANT le geste, la conséquence
 * d'abord, avec la phrase rédigée par le serveur (une seule rédaction pour
 * la synthèse, la fiche de nuit et l'audit).
 *
 * Le même bloc vit dans la synthèse de la clôture et sur la fiche de la
 * nuit ; la fenêtre de confirmation motivée vit dans `DecisionCloture`.
 */

import { AlertTriangle, History, Info } from 'lucide-react'
import { formatMoney } from './money.js'
import type { TraceRemplacementVue, ValidationRemplaceeVue } from './ClosingCommon.js'
import './finance-finalisation.css'

/**
 * Le titre du bloc — jamais un sens qu'on ne connaît pas. Préventes pas
 * encore connues (`closing_known` faux) : ni baisse ni hausse n'est établie,
 * même quand un motif sera exigé faute de preuve.
 */
export function titreRemplacement(r: ValidationRemplaceeVue): string {
  if (!r.closing_known) return 'Finaliser remplacera la validation de la nuit'
  return r.requires_confirmation ? 'Finaliser fera baisser le chiffre de la nuit' : 'Finaliser remplacera la validation de la nuit'
}

export function ValidationRemplacee(props: { r: ValidationRemplaceeVue }) {
  const { r } = props
  // L'ambre seulement quand la baisse est ÉTABLIE : un arrêté inconnu se dit
  // en note neutre (la phrase explique ce qu'on attend).
  const garde = r.requires_confirmation && r.closing_known
  const Icone = garde ? AlertTriangle : Info
  return (
    <div className={garde ? 'frv-bloc frv-bloc--garde' : 'frv-bloc'} role="note">
      <Icone size={18} className="frv-bloc__icone" aria-hidden="true" />
      <div className="frv-bloc__texte">
        <p className="frv-bloc__titre">{titreRemplacement(r)}</p>
        <p className="frv-bloc__phrase">{r.sentence}</p>
        {garde ? (
          <p className="frv-bloc__aide">
            Au-delà de {formatMoney(r.threshold_cents)} de baisse, la finalisation demande un motif, qui reste affiché sur la clôture et sur la fiche de la nuit.
            {' '}Si cet argent doit rester, complétez d’abord la clôture.
          </p>
        ) : null}
        {r.history_label ? <p className="frv-bloc__aide">{r.history_label}.</p> : null}
      </div>
    </div>
  )
}

const FMT_JOUR = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' })
function jour(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : FMT_JOUR.format(d)
}

/**
 * LA TRACE, UNE FOIS LA NUIT FINALISÉE (16/10/2026). La validation en bloc
 * est sortie des totaux et la fiche de nuit ne l'affiche plus : sans ce bloc,
 * le chiffre validé et le motif ne vivaient que dans le journal d'audit.
 */
export function TraceRemplacement(props: { t: TraceRemplacementVue }) {
  const { t } = props
  const quand = jour(t.replaced_at)
  const qui = [quand ? `le ${quand}` : null, t.replaced_by_name ? `par ${t.replaced_by_name}` : null].filter(Boolean).join(' ')
  return (
    <div className="frv-bloc" role="note">
      <History size={18} className="frv-bloc__icone" aria-hidden="true" />
      <div className="frv-bloc__texte">
        <p className="frv-bloc__titre">Validation remplacée à la finalisation</p>
        <p className="frv-bloc__phrase">{t.sentence}</p>
        {t.reason ? <p className="frv-bloc__phrase">Motif : « {t.reason} »</p> : null}
        {qui !== '' ? <p className="frv-bloc__aide">Finalisation {qui}{t.revision > 1 ? ` (révision ${t.revision})` : ''}.</p> : null}
        {t.history_label ? <p className="frv-bloc__aide">{t.history_label}.</p> : null}
      </div>
    </div>
  )
}
